import { useEffect, useState } from "react";
import { AuthorManuscriptNotice } from "@/components/marketplace/AuthorManuscriptNotice";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { PlatformFeeNotice } from "@/components/marketplace/PlatformFeeNotice";
import { BookCoverPicker } from "@/components/marketplace/BookCoverPicker";
import { QuestionnaireEditor } from "@/components/marketplace/QuestionnaireEditor";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

const TYPES = ["novel", "novella", "short_story", "poetry", "memoir", "children", "nonfiction", "screenplay", "play", "other"];
const STYLES = [
  { value: "both", label: "Both — comments + questionnaire" },
  { value: "google_docs", label: "Google Docs / inline comments" },
  { value: "questionnaire", label: "Structured questionnaire" },
];
export const HEAT_LEVELS = [
  { value: 0, label: "Sweet", desc: "No on-page romance / sex" },
  { value: 1, label: "Fade to Black", desc: "Intimacy implied, door closes" },
  { value: 2, label: "Warm", desc: "Sensual scenes, light detail" },
  { value: 3, label: "Open Door", desc: "On-page intimacy, moderate detail" },
  { value: 4, label: "Steamy", desc: "Explicit scenes central to the story" },
  { value: 5, label: "Erotica", desc: "Highly explicit, primary focus" },
];

export default function JobNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editingId } = useParams();
  const isEditing = !!editingId;
  const isAuthor = user?.roles?.includes("author");
  const isAdmin = user?.roles?.includes("admin");
  // Approved Early Access members get a restricted self-serve visibility
  // control on their own projects. "Public" stays admin-only during
  // private beta. (Backend flag name preserved: is_private_test.)
  const isPTA = !!user?.is_private_test;
  const canControlVisibility = isAdmin || isPTA;
  const [form, setForm] = useState({
    title: "", description: "", genre: "", word_count: 60000,
    budget_min: 100, budget_max: 300, currency: "USD",
    turnaround_days: 21, project_type: "novel", feedback_style: "both",
    questionnaire: [],
    spice_level: 0,
    tags: [],
    // A PTA author's Real Opportunity defaults to Early Access Members Only
    // so it's immediately live to the approved test group. Everyone else
    // still starts as Private Draft. Author can flip it later.
    visibility: isPTA ? "private_testers" : "default",
    listing_type: "real",
    real_confirmed: false,
    // Optional book cover — url is the /api/uploads/... path; position is
    // the author-chosen crop framing ({x,y,zoom} percentages).
    cover_url: "",
    cover_position: null,
    // Optional private manuscript URL (Google Docs, Dropbox, OneDrive, etc.).
    // Empty string when not provided.
    manuscript_url: "",
  });
  const [tagDraft, setTagDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [manuscriptFile, setManuscriptFile] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load existing job when editing.
  useEffect(() => {
    if (!isEditing) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${editingId}`);
        if (!alive) return;
        if (data.author_id !== user?.id && !isAdmin) {
          toast.error("You can only edit your own projects.");
          navigate(`/jobs/${editingId}`);
          return;
        }
        setForm((f) => ({
          ...f,
          title: data.title || "",
          description: data.description || "",
          genre: data.genre || "",
          word_count: data.word_count ?? 60000,
          budget_min: data.budget_min ?? 100,
          budget_max: data.budget_max ?? 300,
          currency: data.currency || "USD",
          turnaround_days: data.turnaround_days ?? 21,
          project_type: data.project_type || "novel",
          feedback_style: data.feedback_style || "both",
          spice_level: Number(data.spice_level ?? 0),
          tags: Array.isArray(data.tags) ? data.tags : [],
          visibility: data.visibility || "default",
          listing_type: data.listing_type || "real",
          // Existing rows already passed the confirmation gate at creation;
          // preserve that on edit so admins/users don't have to re-tick.
          real_confirmed: true,
          cover_url: data.cover_url || "",
          cover_position: data.cover_position || null,
          // Editing → hydrate the existing private manuscript URL if the
          // author is allowed to see it (they always are on their own job).
          manuscript_url: data.manuscript_url || "",
        }));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [editingId, isEditing, user?.id, isAdmin, navigate]);

  if (!isAuthor) {
    return (
      <SiteShell>
        <div className="container-page py-16 text-center">
          <h1 className="font-serif text-3xl">Only authors can post projects.</h1>
          <p className="mt-3 text-muted-foreground">Add the Author role from your profile to post beta read requests.</p>
        </div>
      </SiteShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        word_count: Number(form.word_count),
        budget_min: Number(form.budget_min),
        budget_max: Number(form.budget_max),
        turnaround_days: Number(form.turnaround_days),
        spice_level: Number(form.spice_level ?? 0),
        // Book cover — always send both fields so removing the cover
        // (cover_url="") clears it on the server.
        cover_url: form.cover_url || "",
        cover_position: form.cover_url ? (form.cover_position || null) : null,
        // Private manuscript link — always sent so clearing it works too.
        manuscript_url: (form.manuscript_url || "").trim(),
        // listing_type: sent on both create AND edit. The backend accepts
        // reclassification from either the project's own author OR any
        // admin. It's silently stripped for callers who are neither.
        // Visibility is sent for admins (all three options) AND for
        // approved Early Access members on their own projects (backend
        // restricts them to default / private_testers). Regular authors
        // omit the field; backend defaults it to "default".
        ...(canControlVisibility ? { visibility: form.visibility } : {}),
      };
      const { data } = isEditing
        ? await api.patch(`/jobs/${editingId}`, payload)
        : await api.post("/jobs", payload);
      // If the author attached a manuscript, upload it now that we have a job id
      if (manuscriptFile) {
        try {
          const fd = new FormData();
          fd.append("file", manuscriptFile);
          await api.post(`/jobs/${data.id}/manuscript`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (upErr) {
          toast.warning("Project posted, but the manuscript couldn't be uploaded. Try again from the project page.");
        }
      }
      toast.success(isEditing ? "Project updated!" : "Project posted!");
      navigate(`/jobs/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <SiteShell>
        <div className="container-page py-16 text-center text-muted-foreground">Loading project…</div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="container-page py-14 max-w-3xl" data-testid={TID.jobs.createRoot}>
        <h1 className="font-serif text-4xl tracking-tight">
          {isEditing ? "Edit project" : "Post a project"}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {isEditing
            ? "Update your project details. Changes go live immediately."
            : "Share what your book is about and what kind of reader you're hoping to find."}
        </p>

        <form onSubmit={submit} className="mt-10 card-soft space-y-5">
          <Field label="Title">
            <input required minLength={3} maxLength={140} className="sb-input" data-testid={TID.jobs.fTitle}
              value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Contemporary fantasy — 90k, seeking beta reader for pacing feedback" />
          </Field>
          <Field label="Book cover (optional)" hint="Upload a cover image so beta readers can visually identify your book. Drag the preview or use the zoom slider to frame the important part.">
            <BookCoverPicker
              value={{ url: form.cover_url, position: form.cover_position }}
              onChange={(next) => setForm((f) => ({
                ...f,
                cover_url: next.url || "",
                cover_position: next.url ? next.position : null,
              }))}
            />
          </Field>
          <Field label="Description">
            <textarea rows={6} required minLength={10} className="sb-input" data-testid={TID.jobs.fDescription}
              value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="Genre, premise, tone, what you'd love feedback on…" />
            <div className="mt-2 rounded-lg lantern-notice px-3 py-2 text-xs" data-testid="job-new-privacy-reminder">
              <strong className="text-[hsl(var(--sb-gold-2))] dark:text-[hsl(var(--sb-gold))]">Privacy reminder:</strong> Do not include personal contact information in your listing. For your safety, keep applications and communication inside Last Bookmark. Do not post email addresses, phone numbers, payment usernames, private social-media handles, home addresses, or instructions to contact or apply outside the platform.
            </div>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Genre">
              <input required className="sb-input" data-testid={TID.jobs.fGenre}
                value={form.genre} onChange={(e) => set("genre", e.target.value)} placeholder="e.g. Fantasy, Literary, Romance" />
            </Field>
            <Field label="Word count">
              <input type="number" min={0} required className="sb-input" data-testid={TID.jobs.fWordCount}
                value={form.word_count} onChange={(e) => set("word_count", e.target.value)} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Budget min (USD)">
              <input type="number" min={0} required className="sb-input" data-testid={TID.jobs.fBudgetMin}
                value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} />
            </Field>
            <Field label="Budget max (USD)">
              <input type="number" min={0} required className="sb-input" data-testid={TID.jobs.fBudgetMax}
                value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} />
            </Field>
            <Field label="Turnaround (days)">
              <input type="number" min={1} required className="sb-input" data-testid={TID.jobs.fTurnaround}
                value={form.turnaround_days} onChange={(e) => set("turnaround_days", e.target.value)} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Project type">
              <select className="sb-input" data-testid={TID.jobs.fType} value={form.project_type} onChange={(e) => set("project_type", e.target.value)}>
                {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="Feedback style">
              <select className="sb-input" data-testid={TID.jobs.fStyle} value={form.feedback_style} onChange={(e) => set("feedback_style", e.target.value)}>
                {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          {(form.feedback_style === "questionnaire" || form.feedback_style === "both") && (
            <QuestionnaireEditor
              value={form.questionnaire || []}
              onChange={(next) => set("questionnaire", next)}
              testidPrefix="job-new-questionnaire"
            />
          )}
          <Field label="Heat level" hint="Helps beta readers filter by content they're comfortable with.">
            <select
              className="sb-input"
              data-testid="job-new-heat-level"
              value={form.spice_level}
              onChange={(e) => set("spice_level", Number(e.target.value))}
            >
              {HEAT_LEVELS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.value} · {h.label} — {h.desc}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tags">
            <div className="flex gap-2">
              <input className="sb-input flex-1" value={tagDraft} onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = tagDraft.trim(); if (v && !form.tags.includes(v)) set("tags", [...form.tags, v]); setTagDraft(""); } }}
                placeholder="Press Enter to add" />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.tags.map((t, i) => (
                <span key={t} className="chip">
                  {t}
                  <button type="button" onClick={() => set("tags", form.tags.filter((_, idx) => idx !== i))}>×</button>
                </span>
              ))}
            </div>
          </Field>
          <AuthorManuscriptNotice testId="author-manuscript-notice-new" />
          {!isEditing && (
          <Field label="Manuscript (optional)">
            <div className="rounded-xl border-2 border-dashed p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 text-sm">
                {manuscriptFile ? (
                  <>
                    <div className="font-medium truncate">{manuscriptFile.name}</div>
                    <div className="text-xs text-muted-foreground">{(manuscriptFile.size / (1024 * 1024)).toFixed(2)} MB — will attach after publishing</div>
                  </>
                ) : (
                  <div className="text-muted-foreground">PDF, DOC/DOCX, EPUB, TXT, RTF · up to 25 MB · only visible to your accepted reader</div>
                )}
              </div>
              <label className="btn-ghost border cursor-pointer whitespace-nowrap">
                {manuscriptFile ? "Change" : "Attach file"}
                <input type="file" hidden accept=".pdf,.doc,.docx,.epub,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip,text/plain,application/rtf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 25 * 1024 * 1024) { toast.error("File is larger than 25 MB"); return; }
                    setManuscriptFile(f || null);
                  }}
                  data-testid={TID.jobs.manuscriptUpload} />
              </label>
              {manuscriptFile && (
                <button type="button" onClick={() => setManuscriptFile(null)} className="text-sm text-muted-foreground hover:underline">Clear</button>
              )}
            </div>
          </Field>
          )}
          <div>
            <label className="text-sm font-medium mb-1.5 block" htmlFor="job-new-manuscript-url">Manuscript link (optional)</label>
            <p className="text-xs text-muted-foreground mb-2">
              Prefer to work in Google Docs or another online document? Add a private manuscript link here. This link will only be visible to you and beta readers you have accepted for this project.
            </p>
            <input
              id="job-new-manuscript-url"
              type="url"
              className="sb-input"
              placeholder="https://docs.google.com/document/d/…"
              value={form.manuscript_url || ""}
              onChange={(e) => set("manuscript_url", e.target.value)}
              maxLength={1000}
              data-testid="job-new-manuscript-url"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Make sure your document&apos;s sharing permissions allow your accepted reader to access it without requiring you to share personal contact information.
            </p>
          </div>
          {/* Listing type — required for both new and edit flows. */}
          <Field
            label="Project type *"
            hint={
              form.listing_type === "demo"
                ? "Demo/Test projects don't accept applications, Coffee Chats, or payments. Regular users' demos are kept private to you (or your private testing group)."
                : "Real Opportunity projects appear in the marketplace and accept applications and Coffee Chats."
            }
          >
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              data-testid="job-new-listing-type"
            >
              {[
                {
                  value: "real",
                  label: "Real Opportunity",
                  hint: "Real project seeking genuine beta readers",
                },
                {
                  value: "demo",
                  label: "Demo / Test Project",
                  hint: "Example or test listing — no applications or payments",
                },
              ].map((opt) => {
                const active = form.listing_type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      set("listing_type", opt.value);
                      if (opt.value === "demo") set("real_confirmed", false);
                    }}
                    data-testid={`job-new-listing-type-${opt.value}`}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      active
                        ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)]"
                        : "border-border bg-[hsl(var(--card))] !text-[hsl(var(--sb-ink))] hover:bg-[hsl(var(--sb-cream-2))]"
                    }`}
                  >
                    <div className="text-sm font-semibold">{opt.label}</div>
                    <div
                      className={`text-xs mt-0.5 ${
                        active ? "opacity-80" : "!text-[hsl(var(--sb-ink))]/85"
                      }`}
                    >
                      {opt.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Real Opportunity confirmation — hard gate on Publish. */}
          {form.listing_type === "real" && !isEditing && (
            <div
              className="rounded-xl border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/5 p-4"
              data-testid="job-new-real-confirm-block"
            >
              <p className="text-sm text-foreground">
                This project may receive real applications and Coffee Chat requests from other Early Access members. Please confirm that this is a genuine opportunity and not a test listing.
              </p>
              <label className="mt-3 flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border"
                  checked={form.real_confirmed}
                  onChange={(e) => set("real_confirmed", e.target.checked)}
                  data-testid="job-new-real-confirm-checkbox"
                />
                <span className="text-sm">
                  I confirm this is a real opportunity and not a test or example listing.
                </span>
              </label>
              <div className="mt-3">
                <PlatformFeeNotice variant="real" compact />
              </div>
            </div>
          )}

          {canControlVisibility && (() => {
            // Admin gets the full three-way control. Approved PTAs get a
            // two-way choice — no "Public" during private beta.
            const options = [
              { value: "default", label: "Private Draft", hint: "Only you and administrators can view it." },
              { value: "private_testers", label: "Early Access Members Only", hint: "Approved Early Access members and administrators can view and apply." },
              ...(isAdmin ? [{ value: "public", label: "Public", hint: "All eligible beta readers can view and apply." }] : []),
            ];
            const cols = options.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
            return (
              <Field label={isAdmin ? "Project visibility (admin)" : "Project visibility"}>
                <div className={`grid grid-cols-1 ${cols} gap-2`} data-testid="job-new-visibility">
                  {options.map((opt) => {
                    const active = form.visibility === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set("visibility", opt.value)}
                        data-testid={`job-new-visibility-${opt.value}`}
                        className={`text-left rounded-lg border-2 p-3 transition-colors ${
                          active
                            ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)] shadow-sm"
                            : "border-border bg-[hsl(var(--card))] !text-[hsl(var(--sb-ink))] hover:bg-[hsl(var(--sb-cream-2))] hover:border-[hsl(var(--sb-forest))]/40"
                        }`}
                      >
                        <div className="text-sm font-semibold">{opt.label}</div>
                        <div className={`text-xs mt-1 leading-relaxed ${active ? "opacity-95" : "!text-[hsl(var(--sb-ink))]/85"}`}>{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {form.visibility === "default" && (
                  <div className="mt-3 rounded-lg lantern-notice px-3 py-2 text-xs" data-testid="job-new-visibility-warning">
                    <strong className="text-[hsl(var(--sb-gold-2))] dark:text-[hsl(var(--sb-gold))]">Heads up:</strong> Private Draft means beta readers cannot see or apply. Change visibility to <em>Early Access Members Only</em>{isAdmin ? " or " : ""}{isAdmin ? <em>Public</em> : ""} to start receiving applications.
                  </div>
                )}
              </Field>
            );
          })()}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="submit"
              disabled={saving || (!isEditing && form.listing_type === "real" && !form.real_confirmed)}
              className="btn-primary"
              data-testid={TID.jobs.createSubmit}
            >
              {saving
                ? (isEditing ? "Saving…" : "Publishing…")
                : (isEditing ? "Save changes" : "Publish project")}
            </button>
          </div>
        </form>
      </section>
    </SiteShell>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
